/**
 * The manager's task editor inside the shift drawer (TF-WEB-COMPLETION, A), laid out as option F of
 * activities.prototype.html (owner 2026-10-26): the shift as a timeline with every hour, the planned
 * pause striped, an all-day row above it; a range drawn over free time, or a free half hour clicked,
 * opens room under it with the search; a block opens in place.
 *
 * Definitions only: what a task is called, its description and its time
 * window. The status stays with the Tasks component, which saves on its own
 * and is rendered here unchanged, one instance per stored row. Nothing in this
 * file writes. Every change is staged in `TaskEdits` and becomes part of the
 * drawer's one save; Cancel and a dirty close drop the staging and the stored
 * rows are never touched.
 *
 * Why the search asks the server: GET /admin/shift-tasks answers at most twenty
 * rows (backend/controllers/admin/shiftTaskController.js:40), ordered by
 * usage_count then name (:36-39), so a company with more definitions than that
 * could not offer the rest for picking. The same handler already takes a
 * `search` query parameter and matches it against the name with iLike
 * (:8, :16-20), scoped to the active company (:7, :12). The field sends what is
 * typed as that parameter, so any definition is findable by a fragment of its
 * name. No backend change, no paging: the twenty-row cap still applies to each
 * answer, and the search shows the first five.
 *
 * Why the drawer omits `taskAssignments` when nothing is staged: PUT
 * /admin/shifts/:id keeps the rows untouched only when the field is absent
 * (backend/controllers/admin/shiftController.js:820) and deletes and recreates
 * every row whenever it is sent (:822-868). So a time-only edit says nothing
 * about tasks, and only a staged change sends the full list.
 */
import { get } from '@/api/client';
import {
  type ApiShift,
  type ApiTaskAssignment,
  type Step,
  type TaskWrite,
  useActivityPacks,
  useShiftTaskWrites,
} from '@/api/data';
import { T } from '@/i18n';
import { iconName } from '@/pages/innstillinger/IconPick';
import { oslo, osloISO } from '@/rules';
import { Button, toast } from '@/ui';
import { Tasks } from '@/ui/Tasks';
import { fmtDs, hhmm, shiftSpan } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { addDays } from '@timefly/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import te from './TaskEditor.module.css';
import { COLORS, ICONS, activityIcon, colorOf, toneOf } from './activityIcon';

export interface TaskDef {
  name: string;
  description: string;
  has_time_range: boolean;
  /** HH:mm, or empty */
  start_time: string;
  end_time: string;
  /** added «Bare denne gangen» (#202): not saved to the library; absent is false */
  one_off?: boolean;
  /** «Må gjøres» (#202); absent is false */
  required?: boolean;
  /** the steps on this shift (#202); absent is none */
  steps?: Step[];
}
/** A flag or a list set on a definition, the key left out when it is off, so an untouched row equals defOf. */
export function withDef(
  def: TaskDef,
  patch: { one_off?: boolean | undefined; required?: boolean | undefined; steps?: Step[] | undefined },
): TaskDef {
  const { one_off, required, steps, ...rest } = { ...def, ...patch };
  return {
    ...rest,
    ...(one_off ? { one_off } : {}),
    ...(required ? { required } : {}),
    ...(steps?.length ? { steps } : {}),
  };
}
/** Staged changes: an existing row's new definition, null for a staged removal, and the rows to add. */
export interface TaskEdits {
  changed: Partial<Record<number, TaskDef | null>>;
  added: (TaskDef & { key: number })[];
}
export const NO_EDITS: TaskEdits = { changed: {}, added: [] };
const EMPTY: TaskDef = { name: '', description: '', has_time_range: false, start_time: '', end_time: '' };

export function defOf(a: ApiTaskAssignment): TaskDef {
  return {
    name: a.shiftTask?.name ?? '',
    description: a.shiftTask?.description ?? '',
    has_time_range: !!a.has_time_range,
    start_time: a.start_time ? hhmm(a.start_time) : '',
    end_time: a.end_time ? hhmm(a.end_time) : '',
    ...(a.shiftTask?.one_off ? { one_off: true } : {}),
    ...(a.required ? { required: true } : {}),
    ...(a.steps?.length ? { steps: a.steps.map((x) => ({ text: x.text, done: !!x.done })) } : {}),
  };
}
/** The edits that change something: a row opened for editing but left as it was, or a new row left
    empty, is dropped, so opening a task from the day lane is not an unsaved change (review V02). */
export function realEdits(saved: ApiTaskAssignment[], e: TaskEdits): TaskEdits {
  const changed: TaskEdits['changed'] = {};
  for (const a of saved) {
    const c = e.changed[a.id];
    if (c === undefined) continue;
    if (c === null || JSON.stringify(c) !== JSON.stringify(defOf(a))) changed[a.id] = c;
  }
  return {
    changed,
    added: e.added.filter((r) => r.name.trim() || r.start_time || r.end_time || r.description.trim()),
  };
}
export const hasEdits = (e: TaskEdits) => e.added.length > 0 || Object.keys(e.changed).length > 0;

export type { TaskWrite };

function writeOf(def: TaskDef, a?: ApiTaskAssignment): TaskWrite {
  const ranged = def.has_time_range;
  // the #202 fields only where they are or were set: a legacy row's body stays as it was, and the server
  // keeps what is not sent
  const wasOne = !!a?.shiftTask?.one_off;
  return {
    ...(a ? { id: a.id, status: a.status, completedAt: a.completed_at ?? null } : {}),
    shiftTask: {
      name: def.name.trim(),
      description: def.description.trim() || null,
      ...(def.one_off || wasOne ? { one_off: !!def.one_off } : {}),
    },
    has_time_range: ranged,
    start_time: ranged && def.start_time ? def.start_time : null,
    end_time: ranged && def.end_time ? def.end_time : null,
    ...(def.required || a?.required ? { required: !!def.required } : {}),
    ...(def.steps?.length || a?.steps?.length ? { steps: def.steps?.length ? def.steps : null } : {}),
  };
}

/** The definitions the save would carry, stored order first, staged removals left out. */
function rowsOf(saved: ApiTaskAssignment[], edits: TaskEdits): TaskDef[] {
  const kept = saved.flatMap((a) => {
    const c = edits.changed[a.id];
    return c === null ? [] : [c ?? defOf(a)];
  });
  return [...kept, ...edits.added];
}

/** undefined when nothing is staged: the field is then omitted and the API keeps the rows. */
export function tasksBodyOf(saved: ApiTaskAssignment[], edits: TaskEdits): TaskWrite[] | undefined {
  if (!hasEdits(edits)) return undefined;
  const kept = saved.flatMap((a) => {
    const c = edits.changed[a.id];
    return c === null ? [] : [writeOf(c ?? defOf(a), a)];
  });
  return [...kept, ...edits.added.map((d) => writeOf(d))];
}

/**
 * null when the staged rows can be saved; otherwise why not, in the person's words, and the form of
 * the row it is about (`uid`, as the form's ids start), null for a stored row shown without a form.
 */
export function taskProblemAt(
  saved: ApiTaskAssignment[],
  edits: TaskEdits,
): { msg: string; uid: string | null } | null {
  const kept = saved.flatMap((a) => {
    const c = edits.changed[a.id];
    return c === null ? [] : [{ name: (c ?? defOf(a)).name.trim(), uid: c ? `task-${a.id}` : null }];
  });
  const rows = [...kept, ...edits.added.map((r) => ({ name: r.name.trim(), uid: `task-new-${r.key}` }))];
  const empty = rows.find((r) => !r.name);
  if (empty) return { msg: T('Hver aktivitet må ha et navn.'), uid: empty.uid };
  // shift_task_assignments is unique on (shift_id, shift_task_id), so one name twice is a 500
  for (const [i, r] of rows.entries()) {
    const first = rows.findIndex((o) => o.name === r.name);
    if (first === i) continue;
    // the copy that has a form: the later one, else the earlier one being edited
    return { msg: T('To aktiviteter kan ikke ha samme navn.'), uid: r.uid ?? rows[first]?.uid ?? null };
  }
  return null;
}
export const tasksProblem = (saved: ApiTaskAssignment[], edits: TaskEdits) =>
  taskProblemAt(saved, edits)?.msg ?? null;

/**
 * Timed rows that overlap another, as "name HH:MM–HH:MM" pairs. Clock times count from the
 * shift's start, so 23:30–00:30 and 00:00–01:00 on a night shift overlap. Allowed, but said
 * (V19: "Juster tiden om oppgavene ikke kan gjøres samtidig").
 */
export function overlapsOf(
  saved: ApiTaskAssignment[],
  edits: TaskEdits,
  from: string,
  lunch?: { from: string; to: string } | null,
): [string, string][] {
  const m = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
  const f = /^\d{2}:\d{2}/.test(from) ? m(from) : 0;
  const rel = (hhmm: string) => (m(hhmm) - f + 1440) % 1440;
  const lunchRow = lunch
    ? [
        {
          name: T('Lunsj'),
          description: '',
          has_time_range: true,
          start_time: lunch.from,
          end_time: lunch.to,
        },
      ]
    : [];
  const timed = [...rowsOf(saved, edits), ...lunchRow]
    .filter((r) => r.has_time_range && /^\d{2}:\d{2}/.test(r.start_time) && /^\d{2}:\d{2}/.test(r.end_time))
    .map((r) => {
      const a = rel(r.start_time);
      const b = rel(r.end_time);
      return {
        a,
        b: b > a ? b : b + 1440,
        label: `${r.name.trim() || T('Ny aktivitet')} ${r.start_time.slice(0, 5)}–${r.end_time.slice(0, 5)}`,
      };
    });
  const out: [string, string][] = [];
  timed.forEach((x, i) => {
    for (const y of timed.slice(i + 1)) if (x.a < y.b && y.a < x.b) out.push([x.label, y.label]);
  });
  return out;
}

/**
 * Where a new activity goes when nobody said (V19: «prefilled with 30 minutes»): the first quarter
 * hour from the shift's start where `minutes` fit beside the timed rows and the lunch, as clock
 * times. Times count from the shift's start, so a night shift's 01:00 is later than its 23:00. A
 * shift with no free stretch that long gets its first `minutes`; a shift shorter than that, all of it.
 */
export function freeSlot(
  from: string,
  to: string,
  busy: { start_time: string; end_time: string }[],
  minutes = 30,
): { start_time: string; end_time: string } | null {
  if (!/^\d{2}:\d{2}/.test(from) || !/^\d{2}:\d{2}/.test(to)) return null;
  const m = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
  const f = m(from);
  const rel = (hhmm: string) => (m(hhmm) - f + 1440) % 1440;
  const len = rel(to) || 1440;
  const want = Math.min(minutes, len);
  const taken = busy
    .filter((r) => /^\d{2}:\d{2}/.test(r.start_time) && /^\d{2}:\d{2}/.test(r.end_time))
    .map((r) => {
      const a = rel(r.start_time);
      const b = rel(r.end_time);
      return [a, b > a ? b : b + 1440] as const;
    });
  const clock = (x: number) => {
    const v = (f + x) % 1440;
    return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
  };
  let at = 0;
  for (let x = 0; x + want <= len; x += 15)
    if (!taken.some(([a, b]) => x < b && a < x + want)) {
      at = x;
      break;
    }
  return { start_time: clock(at), end_time: clock(at + want) };
}

/** One minute of the shift is this many pixels on the timeline: an 8-hour shift is 480 px tall. */
const PX = 1;
const CLOCK = /^\d{2}:\d{2}/;
/**
 * The shift as minutes from its start, so a night shift's 01:00 comes after its 23:00: `span` places a
 * row (its end at or before its start is the next day's), `clock` turns minutes back into HH:mm, and
 * `hours` are the whole hours to label. Null when the shift has no times yet.
 */
export function timeline(from: string, to: string) {
  if (!CLOCK.test(from) || !CLOCK.test(to)) return null;
  const m = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
  const f = m(from);
  const rel = (hhmm: string) => (m(hhmm) - f + 1440) % 1440;
  const len = rel(to) || 1440;
  const clock = (x: number) => {
    const v = (f + x) % 1440;
    return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
  };
  const hours: number[] = [];
  for (let x = (60 - (f % 60)) % 60; x <= len; x += 60) hours.push(x);
  const span = (r: { start_time: string; end_time: string }): [number, number] => {
    const a = Math.min(rel(r.start_time), len);
    const b = rel(r.end_time);
    return [a, Math.min(b > a ? b : b + 1440, len)];
  };
  return { len, clock, hours, span };
}

export interface CommonTask {
  id: number;
  name: string;
  description: string | null;
  /** the library's chosen icon, null for automatic */
  icon?: string | null;
  /** the library's colour (#202), null is blue */
  color?: string | null;
  /** a name used «Bare denne gangen», answered only when the search asks for them (#202) */
  one_off?: boolean;
  usage_count?: number;
}

/** What the search offers (#202): a library activity, a pack, the typed name once, or as a new activity. */
type Opt =
  | {
      kind: 'use';
      key: string;
      name: string;
      description: string | null;
      icon?: string | null;
      color?: string | null;
    }
  | {
      kind: 'pack';
      key: string;
      name: string;
      icon: string | null;
      color: string | null;
      items: { name: string; minutes: number }[];
    }
  | { kind: 'once' | 'make'; key: string; name: string; seen?: number };

/** What the search field asks for: the most used twenty when nothing is typed, the matches otherwise. */
export function commonTasksPath(term: string): string {
  const q = term.trim();
  return q ? `/admin/shift-tasks?search=${encodeURIComponent(q)}` : '/admin/shift-tasks';
}

/** The most used twenty first, then whatever the search added, each definition once. */
export function mergeCommonTasks(base: CommonTask[], found: CommonTask[]): CommonTask[] {
  const seen = new Set(base.map((o) => o.id));
  return [...base, ...found.filter((o) => !seen.has(o.id))];
}

/** Rows a search offers, as the prototype's «Mest brukt» (owner 2026-10-26, option F). */
const SHOWN = 5;

/**
 * The free stretch a block is in: from the end of what is above it to the start of what is below it,
 * the pause included, so a drag stops at its neighbours (owner 2026-10-26). `busy` is every other timed
 * row in minutes from the shift's start; `len` is the shift's length.
 */
export function stretchOf(busy: [number, number][], a: number, b: number, len: number) {
  return {
    up: Math.max(0, ...busy.filter(([, e]) => e <= a).map(([, e]) => e)),
    down: Math.min(len, ...busy.filter(([s]) => s >= b).map(([s]) => s)),
  };
}

/** A block moved, or its top or bottom edge pulled, by `dm` minutes: kept in its stretch, a quarter hour at least. */
export function dragTo(
  mode: 'move' | 'top' | 'bottom',
  a: number,
  b: number,
  dm: number,
  room: { up: number; down: number },
): [number, number] {
  if (mode === 'top') return [Math.max(room.up, Math.min(b - 15, a + dm)), b];
  if (mode === 'bottom') return [a, Math.min(room.down, Math.max(a + 15, b + dm))];
  const m = Math.max(room.up, Math.min(room.down - (b - a), a + dm));
  return [m, m + (b - a)];
}

/**
 * The nearest free stretch of `len` minutes at or after `from`, else before it, in quarter hours, inside a
 * shift of `total` minutes (the prototype's fit). A start past the end is pulled back so the stretch fits.
 */
export function fitAt(
  busy: [number, number][],
  from: number,
  len: number,
  total: number,
): [number, number] | null {
  if (len <= 0 || len > total) return null;
  const clash = (a: number) => busy.some(([x, y]) => a < y && x < a + len);
  const start = Math.max(0, Math.min(Math.round(from / 15) * 15, total - len));
  for (let m = start; m + len <= total; m += 15) if (!clash(m)) return [m, m + len];
  for (let m = start - 15; m >= 0; m -= 15) if (!clash(m)) return [m, m + len];
  return null;
}

/** One activity to place: its name and description, and a time of its own (`at`) or a length (`minutes`). */
export interface Placing {
  name: string;
  description?: string | null;
  /** minutes from the shift's start, a copied activity's own time */
  at?: [number, number] | null;
  /** a pack's length; 0 or absent with no `at` is «Hele vakten» */
  minutes?: number;
  required?: boolean;
  steps?: Step[] | null;
}
/**
 * A pack or a copied list on this shift (#202): an activity already on it is left out; one with a time of its
 * own keeps it where it is free, else the nearest free stretch; a pack's lengths follow one another from
 * `start`; what finds no room, or has no length, is «Hele vakten». Nothing lands on the pause or another
 * activity. Steps come undone.
 */
export function placeAll(
  items: Placing[],
  o: {
    busy: [number, number][];
    start: number;
    total: number | null;
    onShift: Set<string>;
    clock: (m: number) => string;
  },
): TaskDef[] {
  const busy = [...o.busy];
  const seen = new Set(o.onShift);
  let at = o.start;
  const out: TaskDef[] = [];
  for (const it of items) {
    const name = it.name.trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    const len = it.at ? it.at[1] - it.at[0] : (it.minutes ?? 0);
    const f = o.total == null || len <= 0 ? null : fitAt(busy, it.at ? it.at[0] : at, len, o.total);
    if (f) {
      busy.push(f);
      if (!it.at) at = f[1];
    }
    out.push(
      withDef(
        {
          name,
          description: it.description ?? '',
          has_time_range: !!f,
          start_time: f ? o.clock(f[0]) : '',
          end_time: f ? o.clock(f[1]) : '',
        },
        { required: it.required, steps: (it.steps ?? []).map((x) => ({ text: x.text, done: false })) },
      ),
    );
  }
  return out;
}

/** The pause's new start (#202), inside the shift and off every timed activity; null where it may not go. */
export function pauseFits(timed: [number, number][], len: number, at: number, total: number): number | null {
  if (at < 0 || at + len > total) return null;
  return timed.some(([a, b]) => at < b && a < at + len) ? null : at;
}

/**
 * «Forsinket» (#202): a «Må gjøres» activity not done when its end has passed; one for the whole shift is
 * late when the shift is over. `end` is minutes from the shift's start, `start` the shift's start instant.
 */
export const isLate = (def: TaskDef, done: boolean, start: number, end: number, now = Date.now()): boolean =>
  !!def.required && !done && Number.isFinite(start) && start + end * 60_000 <= now;

/**
 * The timeline in pixels with room opened under what is open: everything from minute `at` on moves down
 * by `px`, so the later hours make room and nothing floats over them (owner 2026-10-26, «Utvid tidslinjen»).
 * `y` is where a minute starts, `yEnd` where something ending at that minute ends.
 */
export function scaleOf(rooms: [number, number][]) {
  const add = (m: number, strict: boolean) =>
    rooms.reduce((s, [at, px]) => s + ((strict ? at < m : at <= m) ? px : 0), 0);
  return {
    y: (m: number) => m * PX + add(m, false),
    yEnd: (m: number) => m * PX + add(m, true),
    extra: add(Number.POSITIVE_INFINITY, true),
  };
}

/** A list answer, or none: an answer that is not a list (a stale cache, an older server) offers nothing. */
const listOf = <X,>(v: X[] | undefined): X[] => (Array.isArray(v) ? v : []);

/** One request per pause in the typing, not one per key. */
function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function TaskEditor({
  tasks,
  edits,
  onChange,
  from = '',
  to = '',
  lunch,
  refused = false,
  date = '',
  shiftId,
  userId,
  onLunch,
}: {
  /** the shift's day, for «Forsinket» and «Kopier fra» (#202) */
  date?: string;
  /** the shift itself, left out of «Kopier fra»; its person's shifts come first there */
  shiftId?: number | null | undefined;
  userId?: number | null | undefined;
  /** the pause moved on the timeline (#202): its new start, HH:mm; absent, the pause stays put */
  onLunch?: ((from: string) => void) | undefined;
  /** a save was refused for these rows: the offending name is marked invalid (review R2) */
  refused?: boolean;
  /** the stored rows, live from the shift list; empty on a new shift */
  tasks: ApiTaskAssignment[];
  /** the shift's start clock, so times after midnight on a night shift compare as later */
  from?: string;
  /** the shift's end clock: the timeline runs from `from` to here */
  to?: string;
  /** the planned pause, drawn on the timeline and kept free of activities */
  lunch?: { from: string; to: string } | null | undefined;
  edits: TaskEdits;
  onChange: (e: TaskEdits) => void;
}) {
  // the company's library: the most used twenty, plus what the typed name matches
  // (GET /admin/shift-tasks, admin only, active company only)
  const [term, setTerm] = useState('');
  // «Lagre som type» (prototype activities F, owner 2026-10-27): a new name gets its icon, colour and
  // description in a dialog before it joins the library; apply puts it on the shift once saved
  const [newType, setNewType] = useState<{
    name: string;
    icon: string | null;
    color: string | null;
    description: string;
    /** the time on this shift, null for «Hele vakten» */
    time: { from: string; to: string } | null;
    /** a stored one-off being saved as a type */
    id?: number | undefined;
    apply: (name: string, description: string, time: { from: string; to: string } | null) => void;
  } | null>(null);
  // the names used «Bare denne gangen», offered at the top of the dialog as in the prototype
  const onceAll = useQuery({
    queryKey: ['shift-tasks', 'once-all'],
    queryFn: () => get<CommonTask[]>('/admin/shift-tasks?include_one_off=1'),
    enabled: !!newType && !newType.id,
    staleTime: 60_000,
  });
  const search = useDebounced(term, 250).trim();
  const base = useQuery({
    queryKey: ['shift-tasks', ''],
    queryFn: () => get<CommonTask[]>(commonTasksPath('')),
    staleTime: 60_000,
  });
  const found = useQuery({
    queryKey: ['shift-tasks', search],
    // one-offs too, only to say how often a name was used once (#202)
    queryFn: () => get<CommonTask[]>(`${commonTasksPath(search)}&include_one_off=1`),
    enabled: search.length > 0,
    staleTime: 60_000,
    // keep the previous matches on screen while the next answer is in flight
    placeholderData: (prev: CommonTask[] | undefined) => prev,
  });
  const options = mergeCommonTasks(
    base.data ?? [],
    listOf(found.data).filter((o) => !o.one_off),
  );
  const onceUses = listOf(found.data).filter((o) => o.one_off);
  const packs = listOf(useActivityPacks().data).filter((p) => Array.isArray(p.items));
  const libWrites = useShiftTaskWrites();
  // «Pakker» and «Kopier fra» above the all-day row (#202), one open at a time; a source opened in it
  const [tool, setTool] = useState<'packs' | 'copy' | null>(null);
  // «Mer» opened in a block, and a block whose steps are being started
  const [more, setMore] = useState<string | null>(null);
  const [stepsFor, setStepsFor] = useState<string | null>(null);
  const [stepText, setStepText] = useState('');
  // the shifts to copy from: the week before this day up to it, asked for when «Kopier fra» opens
  const srcQ = useQuery({
    queryKey: ['task-sources', date],
    queryFn: () =>
      get<{ shifts: ApiShift[] }>(
        `/admin/shifts/date-range?start_date=${addDays(date, -7)}&end_date=${date}`,
      ),
    enabled: tool === 'copy' && /^\d{4}-\d{2}-\d{2}$/.test(date),
    staleTime: 60_000,
  });
  // suggestions (#202, prototype F «forslag fra fredager»): this person's shifts on the same weekday over
  // the four weeks before
  const histQ = useQuery({
    queryKey: ['task-history', date, userId],
    queryFn: () =>
      get<{ shifts: ApiShift[] }>(
        `/admin/shifts/date-range?start_date=${addDays(date, -28)}&end_date=${addDays(date, -1)}`,
      ),
    enabled: !!userId && /^\d{4}-\d{2}-\d{2}$/.test(date),
    staleTime: 300_000,
  });
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // the block opened in place, «task-<id>» or «task-new-<key>»; the one whose activity is being swapped
  const [sel, setSel] = useState<string | null>(null);
  const [ext, setExt] = useState<string[]>([]);
  const [swap, setSwap] = useState<string | null>(null);
  // the highlighted choice in the open search, for the arrow keys and Enter
  const [hi, setHi] = useState(0);
  // «+N til» on the all-day row
  const [allDay, setAllDay] = useState(false);
  // edits that come from outside (a task opened or dragged on the day lane) open the rows they are about;
  // the editor's own changes leave things as they are
  const mine = useRef<TaskEdits | null>(null);
  const emit = (e: TaskEdits) => {
    mine.current = e;
    onChange(e);
  };
  // what a click outside reads: the listener outlives the render it was made in
  const live = useRef({ edits, term });
  live.current = { edits, term };
  useEffect(() => {
    if (edits === mine.current) return;
    // a divider dragged between two tasks opens both
    setExt(
      Object.keys(edits.changed)
        .filter((k) => edits.changed[Number(k)] != null)
        .map((k) => `task-${k}`),
    );
    setSel(null);
    setSwap(null);
  }, [edits]);
  const setChanged = (id: number, v: TaskDef | null | undefined) => {
    const changed = { ...edits.changed };
    if (v === undefined) delete changed[id];
    else changed[id] = v;
    emit({ ...edits, changed });
  };
  const setAdded = (key: number, v: TaskDef | undefined) => {
    const added =
      v === undefined
        ? edits.added.filter((r) => r.key !== key)
        : edits.added.map((r) => (r.key === key ? { ...v, key } : r));
    emit({ ...edits, added });
  };

  // every row the save would carry, as one list: stored ones (staged removals left out), then new ones
  const items = [
    ...tasks.flatMap((a, i) => {
      const c = edits.changed[a.id];
      if (c === null) return [];
      const def = c ?? defOf(a);
      return [
        {
          uid: `task-${a.id}`,
          def,
          saved: a,
          n: i + 1,
          key: undefined as number | undefined,
          set: (v: TaskDef) => setChanged(a.id, v),
        },
      ];
    }),
    ...edits.added.map((r, i) => ({
      uid: `task-new-${r.key}`,
      def: r as TaskDef,
      saved: undefined,
      n: i + 1,
      key: r.key,
      set: (v: TaskDef) => setAdded(r.key, v),
    })),
  ];
  type Item = (typeof items)[number];
  const libOf = (name: string) => options.find((o) => o.name.toLowerCase() === name.trim().toLowerCase());
  const iconOf = (name: string, saved?: ApiTaskAssignment, oneOff?: boolean) =>
    oneOff ? 'clipboard' : activityIcon(name, false, saved?.shiftTask?.icon ?? libOf(name)?.icon);
  /** The colour an item is drawn in (#202): its library row's, slate for a one-off, else blue. */
  const toneFor = (def: TaskDef, saved?: ApiTaskAssignment) =>
    toneOf(saved?.shiftTask?.color ?? libOf(def.name)?.color, !!def.one_off) as React.CSSProperties;
  const line = timeline(from, to);
  // a time being typed is empty halfway (a native time field answers '' until it is whole), so a row
  // keeps its last place on the timeline rather than jumping to the all-day row and losing the cursor
  const lastSpan = useRef<Record<string, [number, number]>>({});
  const span = (it: Item): [number, number] | null => {
    if (!line || !it.def.has_time_range) return null;
    if (CLOCK.test(it.def.start_time) && CLOCK.test(it.def.end_time)) {
      const s = line.span(it.def);
      lastSpan.current[it.uid] = s;
      return s;
    }
    return lastSpan.current[it.uid] ?? null;
  };
  // a new row without a name is the search for its activity (a drawn range, a free half hour, or the
  // all-day row's «Legg til»); the rest are blocks on the timeline or bars in the all-day row
  const drafts = items.filter((it) => !it.saved && !it.def.name.trim());
  const named = items.filter((it) => it.saved || it.def.name.trim());
  const timed = named.filter((it) => span(it));
  const loose = named.filter((it) => !span(it));
  const lunchSpan = line && lunch ? line.span({ start_time: lunch.from, end_time: lunch.to }) : null;
  const spans = (list: Item[]) => list.flatMap((it) => (span(it) ? [span(it) as [number, number]] : []));
  /** what a block may not go over: the pause and every other timed activity */
  const busyBut = (uid: string) => [
    ...spans(timed.filter((it) => it.uid !== uid)),
    ...(lunchSpan ? [lunchSpan] : []),
  ];
  const busy = [...busyBut(''), ...spans(drafts)];

  const problem = taskProblemAt(tasks, edits);
  const overlaps = overlapsOf(tasks, edits, from, lunch);
  // a named row whose name is the problem: it opens and stays open while it is fixed
  const problemUid = problem?.uid && named.some((it) => it.uid === problem.uid) ? problem.uid : null;
  useEffect(() => {
    if (problemUid) setSel(problemUid);
  }, [problemUid]);
  const isOpen = (uid: string) => uid === sel || ext.includes(uid) || uid === problemUid;

  // the opened block and the new one's search are measured, and the timeline opens that much room
  const boxes = useRef(new Map<string, HTMLElement>());
  const box = (uid: string) => (el: HTMLElement | null) => {
    if (el) boxes.current.set(uid, el);
    else boxes.current.delete(uid);
  };
  const [hts, setHts] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const next: Record<string, number> = {};
    for (const [uid, el] of boxes.current) next[uid] = el.offsetHeight;
    const keys = Object.keys(next);
    if (keys.length !== Object.keys(hts).length || keys.some((k) => hts[k] !== next[k])) setHts(next);
  });
  // a new one's block is at least one line tall, its search under it
  const draftH = ([a, b]: [number, number]) => Math.max((b - a) * PX, 30);
  const rooms: [number, number][] = [
    ...timed
      .filter((it) => isOpen(it.uid))
      .map((it) => {
        const [a, b] = span(it) as [number, number];
        return [b, Math.max(0, (hts[it.uid] ?? 0) + 4 - (b - a) * PX)] as [number, number];
      }),
    ...drafts.flatMap((it) => {
      const s = span(it);
      return s ? [[s[1], draftH(s) - (s[1] - s[0]) * PX + 8 + (hts[it.uid] ?? 0)] as [number, number]] : [];
    }),
  ];
  const sc = scaleOf(rooms);
  // the empty half hours, each a place to add (owner 2026-10-26)
  const gaps: number[] = [];
  if (line)
    for (let x = 0; x + 30 <= line.len; x += 30)
      if (!busy.some(([a, b]) => x < b && a < x + 30)) gaps.push(x);

  /** A new row to name: at a time on the timeline, or for the whole shift. One at a time. */
  const startDraft = (at: { start_time: string; end_time: string } | null) => {
    const key = Math.max(0, ...edits.added.map((r) => r.key)) + 1;
    emit({
      ...edits,
      added: [
        ...edits.added.filter((r) => r.name.trim()),
        { ...EMPTY, ...(at ? { has_time_range: true, ...at } : {}), key },
      ],
    });
    setSel(null);
    setExt([]);
    setSwap(null);
    setTerm('');
    setHi(0);
  };
  const dropDrafts = (cur: TaskEdits) => {
    const left = cur.added.filter((r) => r.name.trim());
    if (left.length !== cur.added.length) emit({ ...cur, added: left });
  };
  const closeAll = () => {
    setSel(null);
    setExt([]);
    setSwap(null);
  };
  const shut = (uid: string) => {
    setSel(null);
    setExt((v) => v.filter((x) => x !== uid));
    setSwap(null);
  };

  // a click outside closes what is open; a new one with nothing typed goes, one with a name typed stays,
  // as Outlook keeps it (owner 2026-10-26). The timeline and the all-day row handle their own presses.
  const anyOpen = !!sel || ext.length > 0 || drafts.length > 0;
  // a press on the empty timeline that closed something does not also start a new one
  const swallow = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the listener reads `live`, not the render's values
  useEffect(() => {
    if (!anyOpen) return;
    const down = (e: PointerEvent) => {
      if ((e.target as Element | null)?.closest?.('[data-keep]')) return;
      closeAll();
      if (!live.current.term.trim()) dropDrafts(live.current.edits);
    };
    document.addEventListener('pointerdown', down);
    return () => document.removeEventListener('pointerdown', down);
  }, [anyOpen]);

  // an opened block takes the focus on its name and comes into view
  useEffect(() => {
    if (!sel) return;
    const el = document.getElementById(`${sel}-name`);
    el?.focus({ preventScroll: true });
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [sel]);

  // a block moves when dragged, and its top or bottom edge changes its length, in quarter hours, inside
  // the free stretch it started in; a press that does not move opens it
  const drag = useRef<{
    mode: 'move' | 'top' | 'bottom';
    y: number;
    a: number;
    b: number;
    moved: boolean;
    room: { up: number; down: number };
    def: TaskDef;
    set: (d: TaskDef) => void;
  } | null>(null);
  const done = (it: Item) => it.saved?.status === 'completed';
  const down = (e: React.PointerEvent, it: Item, mode: 'move' | 'top' | 'bottom') => {
    const sp = span(it);
    // a finished activity stays where it was done
    if (!sp || !line || e.button !== 0 || done(it)) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = {
      mode,
      y: e.clientY,
      a: sp[0],
      b: sp[1],
      moved: false,
      room: stretchOf(busyBut(it.uid), sp[0], sp[1], line.len),
      def: it.def,
      set: it.set,
    };
  };
  // a drag over free time draws the new one's range, its times shown, as Outlook does (owner 2026-10-26)
  const draw = useRef<{ a: number; b: number; stop: number } | null>(null);
  const [drawing, setDrawing] = useState<[number, number] | null>(null);
  // the click that ends a drawn range is not a second «add here»
  const drew = useRef(false);
  const lineRef = useRef<HTMLDivElement>(null);
  /** The quarter hour under a pointer, on the timeline's own scale. */
  const minAt = (clientY: number) => {
    const at = clientY - (lineRef.current?.getBoundingClientRect().top ?? 0);
    let best = 0;
    for (let m = 0; line && m <= line.len; m += 15) if (sc.y(m) <= at) best = m;
    return best;
  };
  const move = (e: React.PointerEvent) => {
    const d = draw.current;
    if (d) {
      const b = Math.max(d.a + 15, Math.min(d.stop, minAt(e.clientY) + 15));
      if (b !== d.b) {
        d.b = b;
        setDrawing([d.a, b]);
      }
      return;
    }
    const g = drag.current;
    if (!g || !line) return;
    const dm = Math.round((e.clientY - g.y) / PX / 15) * 15;
    if (!dm && !g.moved) return;
    g.moved = true;
    const [a, b] = dragTo(g.mode, g.a, g.b, dm, g.room);
    g.set({ ...g.def, has_time_range: true, start_time: line.clock(a), end_time: line.clock(b) });
  };
  // a press that moved the block is a drag, so the click that follows it does not open it
  const wasDrag = useRef(false);
  const up = () => {
    const d = draw.current;
    draw.current = null;
    if (d && line) {
      setDrawing(null);
      drew.current = true;
      setTimeout(() => {
        drew.current = false;
      }, 0);
      startDraft({ start_time: line.clock(d.a), end_time: line.clock(d.b) });
      return;
    }
    wasDrag.current = !!drag.current?.moved;
    drag.current = null;
  };
  const cancel = () => {
    draw.current = null;
    setDrawing(null);
    drag.current = null;
  };
  const toggle = (uid: string) => {
    if (wasDrag.current) {
      wasDrag.current = false;
      return;
    }
    dropDrafts(edits);
    setSwap(null);
    setTerm('');
    setSel((v) => (v === uid ? null : uid));
    setExt((v) => v.filter((x) => x !== uid));
  };
  /** The arrow keys move a focused block a quarter hour, inside its stretch. */
  const nudge = (e: React.KeyboardEvent, it: Item) => {
    if ((e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || !line || done(it)) return;
    const sp = span(it);
    if (!sp) return;
    e.preventDefault();
    const [a, b] = dragTo('move', sp[0], sp[1], e.key === 'ArrowDown' ? 15 : -15, {
      ...stretchOf(busyBut(it.uid), sp[0], sp[1], line.len),
    });
    if (a !== sp[0])
      it.set({ ...it.def, has_time_range: true, start_time: line.clock(a), end_time: line.clock(b) });
  };

  // what the open search offers: the library's most used, or its matches to what is typed, never one
  // already on the shift (one shift holds each activity once); in a new one's search the company's packs
  // too; a name the library does not hold is added «Bare denne gangen» or saved as a new activity (#202),
  // with how often it was used once before
  const typed = term.trim();
  const q = typed.toLowerCase();
  const onShift = new Set(rowsOf(tasks, edits).map((r) => r.name.trim().toLowerCase()));
  const isNew = !!typed && !onShift.has(q) && !options.some((o) => o.name.toLowerCase() === q);
  const seen = onceUses.find((o) => o.name.toLowerCase() === q)?.usage_count;
  const optsFor = (draft: boolean): Opt[] => [
    ...options
      .filter((o) => !onShift.has(o.name.toLowerCase()) && (!q || o.name.toLowerCase().includes(q)))
      // the exact name first, so Enter takes it
      .sort((x, y) => Number(y.name.toLowerCase() === q) - Number(x.name.toLowerCase() === q))
      .slice(0, draft && !q && packs.length ? SHOWN - 2 : SHOWN)
      .map(
        (o): Opt => ({
          kind: 'use',
          key: `o${o.id}`,
          name: o.name,
          description: o.description,
          icon: o.icon ?? null,
          color: o.color ?? null,
        }),
      ),
    ...(draft
      ? packs
          .filter((p) => !q || p.name.toLowerCase().includes(q))
          .slice(0, 2)
          .map((p): Opt => ({ kind: 'pack', key: `p${p.id}`, ...p }))
      : []),
    ...(isNew
      ? draft
        ? [
            { kind: 'once', key: 'once', name: typed } as Opt,
            { kind: 'make', key: 'new', name: typed, ...(seen ? { seen } : {}) } as Opt,
          ]
        : [{ kind: 'make', key: 'new', name: typed } as Opt]
      : // nothing typed: «Ny aktivitet …» always last, so making one is never hidden (owner 2026-10-27)
        !typed
        ? [{ kind: 'make', key: 'new', name: '' } as Opt]
        : []),
  ];
  type Pick = (o: Opt) => void;
  const keys = (opts: Opt[], pick: Pick) => (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (opts.length) setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = opts[Math.min(hi, opts.length - 1)];
      if (o) pick(o);
    }
  };
  const list = (opts: Opt[], pick: Pick, allDay = false) => (
    <ul className={te.lib} aria-label={T('Aktivitetsbibliotek')} data-testid="task-library">
      {typed && !opts.some((o) => o.kind === 'use' || o.kind === 'pack') && (
        <li className={te.none}>{T('Ingen treff')}</li>
      )}
      {opts.map((o, i) => (
        <li key={o.key}>
          {/* a library or pack row after the activities says «Pakker» once */}
          {o.kind === 'pack' && opts[i - 1]?.kind !== 'pack' && !typed && (
            <span className={`${te.lbl} ${te.sub}`}>{T('Pakker')}</span>
          )}
          {/* a line before making a new one, as the prototype */}
          {o.kind === 'make' && i > 0 && opts[i - 1]?.kind !== 'once' && <hr className={te.sep} />}
          <button
            type="button"
            className={`${te.row}${i === hi ? ` ${te.hi}` : ''}${o.kind === 'make' ? ` ${te.new}` : ''}`}
            data-testid={
              o.kind === 'make'
                ? 'task-create'
                : o.kind === 'once'
                  ? 'task-once'
                  : o.kind === 'pack'
                    ? 'task-pack'
                    : 'task-pick'
            }
            // the search keeps the cursor while a row is pressed
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(o)}
          >
            <span
              className={te.tile}
              style={
                (o.kind === 'use' || o.kind === 'pack'
                  ? toneOf(o.color)
                  : o.kind === 'once'
                    ? toneOf(null, true)
                    : undefined) as React.CSSProperties
              }
            >
              <Icon
                name={
                  o.kind === 'make'
                    ? 'plus'
                    : o.kind === 'once'
                      ? 'clipboard'
                      : o.kind === 'pack'
                        ? activityIcon(o.name, false, o.icon ?? 'folder')
                        : activityIcon(o.name, false, 'icon' in o ? o.icon : null)
                }
              />
            </span>
            <span>
              {o.kind === 'make'
                ? o.name
                  ? T('Lagre som type «{name}» …', { name: o.name })
                  : T('Ny type …')
                : o.kind === 'once'
                  ? T('Bare denne gangen')
                  : o.name}
            </span>
            {o.kind === 'pack' && (
              <small className={te.meta}>{T('{n} aktiviteter', { n: o.items.length })}</small>
            )}
            {o.kind === 'use' && allDay && <small className={te.meta}>{T('Hele vakten')}</small>}
            {o.kind === 'make' && o.seen ? (
              <small className={te.meta}>
                {o.seen === 1 ? T('brukt 1 gang') : T('brukt {n} ganger', { n: o.seen })}
              </small>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
  /** New rows on the shift, a placed pack or a copied list, with the new one being named (`drop`) gone. */
  const addRows = (defs: TaskDef[], drop?: number) => {
    let key = Math.max(0, ...edits.added.map((r) => r.key));
    emit({
      ...edits,
      added: [
        ...edits.added.filter((r) => r.key !== drop && r.name.trim()),
        ...defs.map((d) => ({ ...d, key: ++key })),
      ],
    });
    setTerm('');
    closeAll();
  };
  /** Where a pack or a copy may land: the pause and every activity with a time, from `start`. */
  const placing = (items: Placing[], start: number, drop?: number) =>
    addRows(
      placeAll(items, {
        busy: busyBut(''),
        start,
        total: line?.len ?? null,
        onShift,
        clock: (m) => line?.clock(m) ?? '',
      }),
      drop,
    );
  /** A clock time on another shift as minutes from this one's start; null without times. */
  const relOf = (hm: string | null | undefined) => {
    if (!line || !hm || !CLOCK.test(hm) || !CLOCK.test(from)) return null;
    const m = (x: string) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5));
    return (m(hm) - m(from) + 1440) % 1440;
  };
  const fromAssignment = (a: ApiTaskAssignment): Placing => {
    const a0 = a.has_time_range ? relOf(a.start_time) : null;
    const b0 = a.has_time_range ? relOf(a.end_time) : null;
    const len = a0 != null && b0 != null ? (b0 - a0 + 1440) % 1440 || 1440 : 0;
    return {
      name: a.shiftTask?.name ?? '',
      description: a.shiftTask?.description ?? null,
      at: a0 != null && len > 0 ? [a0, a0 + len] : null,
      required: !!a.required,
      steps: a.steps ?? null,
    };
  };
  const onTerm = (v: string) => {
    setTerm(v);
    setHi(0);
  };

  /**
   * The row's problem, said once the person has tried to save or has named the row it is about: an
   * empty new row is not an error before anyone asked for it to be saved (gap A2).
   */
  const errFor = (uid: string, def: TaskDef) =>
    problem?.uid === uid && (refused || def.name.trim() !== '') ? problem : null;
  const clockOf = (d: TaskDef) => `${d.start_time.slice(0, 5)}–${d.end_time.slice(0, 5)}`;
  // «Forsinket» reads the shift's start as an instant (#202)
  const shiftStart = date && CLOCK.test(from) ? Date.parse(osloISO(date, from)) : Number.NaN;
  const lateOf = (it: Item) => isLate(it.def, done(it), shiftStart, span(it)?.[1] ?? line?.len ?? 0);
  /** The marks at the end of the name's line: late, the steps as «x av y», done (#202). */
  const mark = (it: Item) => {
    const st = it.def.steps ?? [];
    const n = st.filter((x) => x.done).length;
    const fin = done(it) || (st.length > 0 && n === st.length);
    return (
      <>
        {lateOf(it) && (
          <span className={`${te.mk} ${te.bad}`} data-testid="task-late">
            {/* an icon beside the word, so the state is not the red alone (#280 Vaktplan C, overdue) */}
            <Icon name="warn" />
            {T('Forsinket')}
          </span>
        )}
        {st.length > 0 && !fin && (
          <span className={`${te.mk} ${te.cnt}`} title={T('Steg')} data-testid="task-steps-count">
            {T('{n} av {m}', { n, m: st.length })}
          </span>
        )}
        {fin && (
          <span className={te.mk} title={T('Fullført')}>
            <Icon name="check" />
            {st.length > 0 && ` ${T('{n} av {m}', { n, m: st.length })}`}
          </span>
        )}
      </>
    );
  };
  const flags = (d: TaskDef) => ({ one_off: d.one_off, required: d.required, steps: d.steps });
  /** «Lagre som type» for a row: its name, description and time to start from, and how the answer lands. */
  const openType = (it: Item, d: TaskDef, name: string, id?: number) =>
    setNewType({
      name,
      icon: null,
      color: null,
      description: d.description,
      time: d.has_time_range && d.start_time && d.end_time ? { from: d.start_time, to: d.end_time } : null,
      id,
      apply: (n, description, time) =>
        it.set(
          withDef(
            {
              ...d,
              name: n,
              description,
              has_time_range: !!time,
              start_time: time?.from ?? '',
              end_time: time?.to ?? '',
            },
            { ...flags(d), one_off: false },
          ),
        ),
    });
  /** A step added, ticked or removed on this shift only (#202); the row is staged as any change. */
  const setSteps = (it: Item, steps: Step[]) => it.set(withDef(it.def, { ...flags(it.def), steps }));

  /** A new row's search: in the room under its drawn range, or under the all-day row. */
  const draftBox = (it: Item, style?: React.CSSProperties) => {
    const err = errFor(it.uid, it.def);
    // «Legg til» in Hele vakten: library names placed for the whole shift, no packs (prototype F)
    const allDay = !span(it);
    const opts = optsFor(true).filter((o) => !allDay || o.kind !== 'pack');
    const choose: Pick = (o) => {
      setTerm('');
      // a pack takes the new one's place: its activities one after another from where it was drawn
      if (o.kind === 'pack') {
        placing(
          o.items.map((x) => ({ name: x.name, minutes: x.minutes })),
          span(it)?.[0] ?? 0,
          it.key,
        );
        return;
      }
      if (o.kind === 'make') {
        openType(it, it.def, o.name);
        return;
      }
      it.set(
        withDef(
          {
            ...it.def,
            name: o.name,
            description: it.def.description || (o.kind === 'use' ? (o.description ?? '') : ''),
          },
          { one_off: o.kind === 'once', required: it.def.required, steps: it.def.steps },
        ),
      );
    };
    return (
      <div
        key={it.uid}
        ref={box(it.uid)}
        className={te.pop}
        style={style}
        data-testid="task-new"
        data-uid={it.uid}
      >
        <div className={te.q}>
          <input
            id={`${it.uid}-name`}
            className="inp w"
            type="search"
            autoComplete="off"
            aria-label={T('Aktivitet {t}', { t: span(it) ? clockOf(it.def) : T('Hele vakten') })}
            aria-describedby={err ? `${it.uid}-name-err` : undefined}
            aria-invalid={!!err || undefined}
            // biome-ignore lint/a11y/noAutofocus: the person just asked for this row, so the cursor goes to its search
            autoFocus
            value={term}
            placeholder={T('Søk eller skriv et nytt navn')}
            onChange={(e) => onTerm(e.target.value)}
            onKeyDown={keys(opts, choose)}
          />
          <button
            type="button"
            className={te.x}
            aria-label={T('Fjern ny aktivitet {n}', { n: it.n })}
            onClick={() => {
              setTerm('');
              setAdded(it.key as number, undefined);
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        {err && (
          <p className="ferr" id={`${it.uid}-name-err`} data-testid="task-problem">
            {err.msg}
          </p>
        )}
        {!typed && opts.length > 0 && <span className={te.lbl}>{T('Mest brukt')}</span>}
        {list(opts, choose, allDay)}
      </div>
    );
  };

  /** A block opened in place: its coloured top, then its time, its description and Fjern. */
  const openBox = (it: Item, style?: React.CSSProperties) => {
    const def = it.def;
    const name = def.name.trim();
    const err = errFor(it.uid, def);
    const fin = done(it);
    const changed = it.saved && edits.changed[it.saved.id] !== undefined;
    const label = it.saved?.shiftTask?.name ?? name;
    // a placed activity changes to another, keeping its time and description; a library one is no one-off
    const swapOpts = optsFor(false);
    const pick: Pick = (o) => {
      setTerm('');
      setSwap(null);
      if (o.kind === 'make') {
        openType(it, def, o.name);
        return;
      }
      it.set(
        withDef(
          {
            ...def,
            name: o.name,
            description: def.description || (o.kind === 'use' ? (o.description ?? '') : ''),
          },
          { one_off: false, required: def.required, steps: def.steps },
        ),
      );
    };
    return (
      <div
        key={it.uid}
        ref={box(it.uid)}
        className={te.open}
        style={{ ...style, ...toneFor(def, it.saved) }}
        data-testid={it.saved ? 'task-edit' : 'task-new'}
        data-uid={it.uid}
      >
        <fieldset className={te.fs} data-testid="task-form">
          <legend className="sr">
            {it.saved ? T('Aktivitet {n}', { n: it.n }) : T('Ny aktivitet {n}', { n: it.n })}
          </legend>
          {/* a press on the title bar folds the activity, as the panel's sections (owner 2026-10-27) */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: the ✕ and Esc close it from the keyboard */}
          <div
            className={`${te.head} ${te.headFold}`}
            onClick={(e) => {
              if (!(e.target as Element).closest('button, input, select, textarea')) shut(it.uid);
            }}
          >
            <span className={te.tile}>
              <Icon name={iconOf(name, it.saved, def.one_off)} />
            </span>
            <button
              id={`${it.uid}-name`}
              type="button"
              className={te.swap}
              title={T('Bytt aktivitet')}
              aria-expanded={swap === it.uid}
              aria-describedby={err ? `${it.uid}-name-err` : undefined}
              aria-invalid={(refused && !!err) || undefined}
              onClick={() => {
                setSwap((v) => (v === it.uid ? null : it.uid));
                onTerm('');
              }}
            >
              <span>{name}</span>
              <Icon name="chevronDown" />
            </button>
            {mark(it)}
            <span className={te.sp} />
            <button type="button" className={te.x} aria-label={T('Lukk')} onClick={() => shut(it.uid)}>
              <Icon name="close" />
            </button>
          </div>
          {err && (
            <p className="ferr" id={`${it.uid}-name-err`} data-testid="task-problem">
              {err.msg}
            </p>
          )}
          {swap === it.uid && (
            <div className={te.swapq}>
              <input
                className="inp w"
                type="search"
                autoComplete="off"
                aria-label={T('Bytt aktivitet')}
                // biome-ignore lint/a11y/noAutofocus: the person just asked to change the activity
                autoFocus
                value={term}
                placeholder={T('Bytt til …')}
                onChange={(e) => onTerm(e.target.value)}
                onKeyDown={keys(swapOpts, pick)}
              />
              {list(swapOpts, pick)}
            </div>
          )}
          {/* a time of its own, or the whole shift (owner 2026-10-26) */}
          <fieldset className="seg" aria-label={T('Tid')}>
            <button
              type="button"
              aria-pressed={def.has_time_range}
              data-testid="task-fixed"
              onClick={() =>
                !def.has_time_range &&
                it.set({
                  ...def,
                  has_time_range: true,
                  ...(freeSlot(from, to, [
                    ...rowsOf(tasks, edits),
                    ...(lunch ? [{ start_time: lunch.from, end_time: lunch.to }] : []),
                  ]) ?? {}),
                })
              }
            >
              {T('Fast tid')}
            </button>
            <button
              type="button"
              aria-pressed={!def.has_time_range}
              data-testid="task-anytime"
              onClick={() =>
                def.has_time_range && it.set({ ...def, has_time_range: false, start_time: '', end_time: '' })
              }
            >
              {T('Hele vakten')}
            </button>
          </fieldset>
          {def.has_time_range && (
            <div className={te.when}>
              <input
                id={`${it.uid}-from`}
                className="inp"
                type="time"
                aria-label={T('Fra')}
                disabled={fin}
                value={def.start_time}
                onChange={(e) => it.set({ ...def, start_time: e.target.value, has_time_range: true })}
              />
              <span>{T('til')}</span>
              <input
                className="inp"
                type="time"
                aria-label={T('Til')}
                disabled={fin}
                value={def.end_time}
                onChange={(e) => it.set({ ...def, end_time: e.target.value, has_time_range: true })}
              />
            </div>
          )}
          {/* the status and who finished it; it saves on its own, as before (TF-WEB-COMPLETION) */}
          {it.saved && (
            <div className={te.status}>
              <Tasks
                tasks={[
                  {
                    ...it.saved,
                    has_time_range: false,
                    shiftTask: it.saved.shiftTask && { ...it.saved.shiftTask, description: null },
                  },
                ]}
              />
            </div>
          )}
          {(def.steps?.length || stepsFor === it.uid) && (
            <div className={te.steps} data-testid="task-steps">
              <span className={te.lbl}>
                {T('Steg, {n} av {m}', {
                  n: (def.steps ?? []).filter((x) => x.done).length,
                  m: def.steps?.length ?? 0,
                })}
              </span>
              {(def.steps ?? []).map((x, k) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a step is its place in the list; two may read alike
                <div className={te.step} key={k}>
                  <label>
                    <input
                      type="checkbox"
                      checked={x.done}
                      onChange={(e) =>
                        setSteps(
                          it,
                          (def.steps ?? []).map((y, j) => (j === k ? { ...y, done: e.target.checked } : y)),
                        )
                      }
                    />
                    <span>{x.text}</span>
                  </label>
                  <button
                    type="button"
                    className={te.x}
                    aria-label={T('Fjern steget {s}', { s: x.text })}
                    onClick={() =>
                      setSteps(
                        it,
                        (def.steps ?? []).filter((_, j) => j !== k),
                      )
                    }
                  >
                    <Icon name="close" />
                  </button>
                </div>
              ))}
              <input
                id={`${it.uid}-step`}
                className="inp w"
                aria-label={T('Legg til steg')}
                placeholder={T('Legg til steg')}
                value={stepText}
                onChange={(e) => setStepText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !stepText.trim()) return;
                  e.preventDefault();
                  setSteps(it, [...(def.steps ?? []), { text: stepText.trim().slice(0, 200), done: false }]);
                  setStepText('');
                }}
              />
            </div>
          )}
          {def.one_off && (
            <p className={te.once} data-testid="task-oneoff">
              {T('Bare denne gangen.')}{' '}
              <button
                type="button"
                className="lnk"
                // a stored one-off joins the library from the dialog, so the next shift finds it
                onClick={() =>
                  openType(
                    it,
                    def,
                    def.name,
                    it.saved?.shiftTask?.one_off ? it.saved.shiftTask.id : undefined,
                  )
                }
              >
                {T('Lagre som aktivitet')}
              </button>
            </p>
          )}
          <label className={te.fl}>
            <span className={te.lbl}>{T('Beskjed til den ansatte')}</span>
            <input
              id={`${it.uid}-desc`}
              className="inp w"
              aria-label={T('Beskrivelse')}
              value={def.description}
              placeholder={T('Valgfritt')}
              onChange={(e) => it.set({ ...def, description: e.target.value })}
            />
          </label>
          {more === it.uid && (
            <div className={te.more} data-testid="task-more-box">
              {!def.steps?.length && stepsFor !== it.uid && (
                <div className={te.mrow}>
                  <span>{T('Steg')}</span>
                  <button
                    type="button"
                    className="lnk"
                    onClick={() => {
                      setStepsFor(it.uid);
                      setStepText('');
                      requestAnimationFrame(() => document.getElementById(`${it.uid}-step`)?.focus());
                    }}
                  >
                    {T('Legg til')}
                  </button>
                </div>
              )}
              <label className={te.mrow}>
                <span>{T('Må gjøres')}</span>
                <input
                  type="checkbox"
                  data-testid="task-required"
                  checked={!!def.required}
                  onChange={(e) => it.set(withDef(def, { ...flags(def), required: e.target.checked }))}
                />
              </label>
            </div>
          )}
          <div className={te.acts}>
            <Button
              small
              ghost
              tone="dgr"
              aria-label={
                it.saved ? T('Fjern {task}', { task: label }) : T('Fjern ny aktivitet {n}', { n: it.n })
              }
              onClick={() => {
                if (it.saved) setChanged(it.saved.id, null);
                else setAdded(it.key as number, undefined);
                shut(it.uid);
              }}
            >
              {T('Fjern')}
            </Button>
            <span className={te.sp} />
            <Button
              small
              ghost
              aria-expanded={more === it.uid}
              data-testid="task-more"
              onClick={() => setMore((v) => (v === it.uid ? null : it.uid))}
            >
              {more === it.uid ? T('Mindre') : T('Mer')}
            </Button>
            {changed && (
              <Button
                small
                ghost
                aria-label={T('Angre endring av {task}', { task: label })}
                onClick={() => {
                  setChanged((it.saved as ApiTaskAssignment).id, undefined);
                  shut(it.uid);
                }}
              >
                {T('Angre')}
              </Button>
            )}
          </div>
        </fieldset>
      </div>
    );
  };

  // the pause being dragged: where the press began and the pause's start then
  const pauseDrag = useRef<{ y: number; a: number } | null>(null);
  /** Where the pause may start: inside the shift and off every timed activity, else null. */
  const pauseTo = (cur: [number, number], at: number) =>
    line ? pauseFits(spans(timed.concat(drafts)), cur[1] - cur[0], at, line.len) : null;

  // the suggestions: on at least two of those days (or the one, with one), not on this shift, not hidden,
  // a timed one only where it fits; most often first
  const weekday = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T12:00:00Z`).getUTCDay() : -1;
  const past = (histQ.data?.shifts ?? []).filter((x) => {
    const d = oslo(x.start_time)?.date;
    return (
      x.user_id === userId && x.id !== shiftId && !!d && new Date(`${d}T12:00:00Z`).getUTCDay() === weekday
    );
  });
  const ghostMap = new Map<string, { p: Placing; n: number; a: ApiTaskAssignment }>();
  for (const x of past)
    for (const a of x.taskAssignments ?? []) {
      const n = a.shiftTask?.name?.trim();
      if (!n || a.shiftTask?.one_off) continue;
      const g = ghostMap.get(n);
      if (g) g.n++;
      else ghostMap.set(n, { p: fromAssignment(a), n: 1, a });
    }
  const fitsNow = (at: [number, number] | null | undefined) =>
    !at || (!!line && at[1] <= line.len && !busy.some(([a, b]) => at[0] < b && a < at[1]));
  const ghosts = [...ghostMap.entries()]
    .filter(
      ([n, g]) =>
        g.n >= Math.min(2, past.length) && !onShift.has(n.toLowerCase()) && !hidden.has(n) && fitsNow(g.p.at),
    )
    .sort((x, y) => y[1].n - x[1].n)
    .map(([name, g]) => ({ name, ...g }));
  const DAYS = ['søndager', 'mandager', 'tirsdager', 'onsdager', 'torsdager', 'fredager', 'lørdager'];
  const ghostDay = T(DAYS[weekday] ?? 'dager');
  const ghostIcon = (g: (typeof ghosts)[number]) =>
    activityIcon(g.name, false, g.a.shiftTask?.icon ?? libOf(g.name)?.icon);
  const ghostTone = (g: (typeof ghosts)[number]) =>
    toneOf(g.a.shiftTask?.color ?? libOf(g.name)?.color) as React.CSSProperties;
  const keep = (gs: typeof ghosts) =>
    placing(
      gs.map((g) => g.p),
      0,
    );
  // «Kopier fra» (#202): the same day's shifts first, then this person's, the latest first; five at most
  const sources = (srcQ.data?.shifts ?? [])
    .filter((x) => x.id !== shiftId && (x.taskAssignments?.length ?? 0) > 0)
    .map((x) => ({ x, day: oslo(x.start_time)?.date ?? '' }))
    .sort(
      (p, r) =>
        Number(r.day === date) - Number(p.day === date) ||
        Number(r.x.user_id === userId) - Number(p.x.user_id === userId) ||
        r.x.start_time.localeCompare(p.x.start_time),
    )
    .slice(0, 5);
  const srcLabel = ({ x, day }: { x: ApiShift; day: string }) =>
    `${day === date ? T('Samme dag') : fmtDs(day)} ${shiftSpan(x.start_time, x.end_time)}${x.user?.name ? `, ${x.user.name}` : ''}`;
  const onIt = (name: string) => onShift.has(name.trim().toLowerCase());
  const leftOf = (names: string[]) => names.filter((n) => !onIt(n)).length;
  const tools = (
    <>
      <div className={te.tools} data-keep>
        <button
          type="button"
          className={`${te.tool}${tool === 'packs' ? ` ${te.on}` : ''}`}
          aria-expanded={tool === 'packs'}
          data-testid="task-packs-open"
          onClick={() => setTool((v) => (v === 'packs' ? null : 'packs'))}
        >
          <Icon name="folder" />
          {T('Pakker')}
          <Icon name="chevronDown" />
        </button>
        <button
          type="button"
          className={`${te.tool}${tool === 'copy' ? ` ${te.on}` : ''}`}
          aria-expanded={tool === 'copy'}
          data-testid="task-copy-open"
          onClick={() => setTool((v) => (v === 'copy' ? null : 'copy'))}
        >
          <Icon name="calendar" />
          {T('Kopier fra')}
          <Icon name="chevronDown" />
        </button>
      </div>
      {tool === 'packs' && (
        <div className={te.dd} data-testid="task-packs" data-keep>
          {packs.length ? (
            packs.map((p) => {
              const left = leftOf(p.items.map((i) => i.name));
              return (
                <div className={te.src} key={p.id} data-testid="task-pack-box">
                  <div className={te.srch}>
                    <span className={te.tile} style={toneOf(p.color) as React.CSSProperties}>
                      <Icon name={activityIcon(p.name, false, p.icon ?? 'folder')} />
                    </span>
                    <b>{p.name}</b>
                    <span className={te.sp} />
                    {left ? (
                      <button
                        type="button"
                        className="lnk"
                        onClick={() => {
                          placing(
                            p.items.map((i) => ({ name: i.name, minutes: i.minutes })),
                            0,
                          );
                          setTool(null);
                        }}
                      >
                        {left === p.items.length ? T('Legg til alle') : T('Legg til {n}', { n: left })}
                      </button>
                    ) : (
                      <small className={te.meta}>{T('alle på vakten')}</small>
                    )}
                  </div>
                  {p.items.map((i) => (
                    <div className={`${te.item}${onIt(i.name) ? ` ${te.dim}` : ''}`} key={i.name}>
                      <span className={te.tile} style={toneOf(libOf(i.name)?.color) as React.CSSProperties}>
                        <Icon name={activityIcon(i.name, false, libOf(i.name)?.icon)} />
                      </span>
                      <span>{i.name}</span>
                      <small className={te.meta}>
                        {onIt(i.name) ? T('på vakten') : i.minutes ? `${i.minutes} min` : T('Hele vakten')}
                      </small>
                    </div>
                  ))}
                </div>
              );
            })
          ) : (
            <p className="hint">{T('Ingen pakker ennå. De lages under Innstillinger, Aktiviteter.')}</p>
          )}
        </div>
      )}
      {tool === 'copy' && (
        <div className={te.dd} data-testid="task-copy" data-keep>
          {srcQ.isPending ? (
            <p className="hint">{T('Henter vakter …')}</p>
          ) : sources.length ? (
            sources.map((src) => {
              const items = src.x.taskAssignments ?? [];
              const left = leftOf(items.map((a) => a.shiftTask?.name ?? ''));
              return (
                <div className={te.src} key={src.x.id} data-testid="task-source">
                  <div className={te.srch}>
                    <b>{srcLabel(src)}</b>
                    <span className={te.sp} />
                    {left ? (
                      <button
                        type="button"
                        className="lnk"
                        onClick={() => {
                          placing(items.map(fromAssignment), 0);
                          setTool(null);
                        }}
                      >
                        {left === items.length ? T('Kopier alle') : T('Kopier {n}', { n: left })}
                      </button>
                    ) : (
                      <small className={te.meta}>{T('alle på vakten')}</small>
                    )}
                  </div>
                  {items.map((a) => {
                    const n = a.shiftTask?.name ?? '';
                    const on = onIt(n);
                    return (
                      <button
                        type="button"
                        key={a.id}
                        className={te.row}
                        disabled={on}
                        data-testid="task-source-item"
                        onClick={() => placing([fromAssignment(a)], 0)}
                      >
                        <span
                          className={te.tile}
                          style={toneOf(a.shiftTask?.color, !!a.shiftTask?.one_off) as React.CSSProperties}
                        >
                          <Icon
                            name={
                              a.shiftTask?.one_off ? 'clipboard' : activityIcon(n, false, a.shiftTask?.icon)
                            }
                          />
                        </span>
                        <span>{n}</span>
                        <small className={te.meta}>
                          {on
                            ? T('på vakten')
                            : a.has_time_range && a.start_time && a.end_time
                              ? `${hhmm(a.start_time)}–${hhmm(a.end_time)}`
                              : T('Hele vakten')}
                        </small>
                        {!on && <Icon name="plus" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <p className="hint">{T('Ingen andre vakter med aktiviteter siste uke.')}</p>
          )}
        </div>
      )}
    </>
  );

  // the all-day row: two bars, then «+N til»; one opened further down is shown with them
  const bars = allDay || loose.slice(2).some((it) => isOpen(it.uid)) ? loose : loose.slice(0, 2);
  const looseDrafts = drafts.filter((it) => !span(it));

  return (
    <div
      className="tasks"
      data-testid="task-editor"
      onKeyDown={(e) => {
        // Escape closes what is open here, not the shift editor around it
        if (e.key !== 'Escape' || !(swap || anyOpen)) return;
        e.preventDefault();
        e.stopPropagation();
        if (swap) {
          setSwap(null);
          setTerm('');
          return;
        }
        const back = sel ?? ext[0];
        closeAll();
        dropDrafts(edits);
        setTerm('');
        if (back)
          requestAnimationFrame(() =>
            document.querySelector<HTMLElement>(`[data-blk="${back}"] button, [data-bar="${back}"]`)?.focus(),
          );
      }}
    >
      {tools}
      {ghosts.length > 0 && (
        <div className={te.sug} data-testid="task-suggest">
          <Icon name="sparkle" />
          <span>
            {ghosts.length === 1
              ? T('1 forslag fra {day}', { day: ghostDay })
              : T('{n} forslag fra {day}', { n: ghosts.length, day: ghostDay })}
          </span>
          <button type="button" className="lnk" onClick={() => keep(ghosts)}>
            {T('Legg til alle')}
          </button>
          <button
            type="button"
            className={te.x}
            aria-label={T('Skjul forslagene')}
            title={T('Skjul')}
            onClick={() => setHidden(new Set([...hidden, ...ghosts.map((g) => g.name)]))}
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      {/* «Hele vakten» as a calendar's all-day row, above the hours (owner 2026-10-26) */}
      <div className={te.wrow} data-keep>
        <span className={te.wl}>{line ? T('Hele vakten') : T('Aktiviteter')}</span>
        <div className={te.wbars}>
          {ghosts
            .filter((g) => !g.p.at)
            .map((g) => (
              <button
                key={`g-${g.name}`}
                type="button"
                className={`${te.wbar} ${te.ghost}`}
                style={ghostTone(g)}
                title={T('{n} av {m} {day}', { n: g.n, m: past.length, day: ghostDay })}
                aria-label={T('Legg til {task}', { task: g.name })}
                onClick={() => keep([g])}
              >
                <Icon name={ghostIcon(g)} />
                <span>{g.name}</span>
                <small className={te.gof}>{T('{n} av {m}', { n: g.n, m: past.length })}</small>
                <Icon name="plus" />
              </button>
            ))}
          {bars.map((it) =>
            isOpen(it.uid) ? (
              openBox(it)
            ) : (
              <button
                key={it.uid}
                type="button"
                className={te.wbar}
                style={toneFor(it.def, it.saved)}
                data-bar={it.uid}
                aria-label={T('Rediger {task}', { task: it.def.name.trim() })}
                aria-expanded={false}
                onClick={() => toggle(it.uid)}
              >
                <Icon name={iconOf(it.def.name, it.saved, it.def.one_off)} />
                <span>{it.def.name.trim()}</span>
                {mark(it)}
              </button>
            ),
          )}
          {looseDrafts.map((it) => draftBox(it))}
          <div className={te.wfoot}>
            {loose.length > 2 && (
              <button type="button" className="lnk" onClick={() => setAllDay((v) => !v)}>
                {allDay ? T('Vis færre') : T('+{n} til', { n: loose.length - 2 })}
              </button>
            )}
            <button
              type="button"
              className={`lnk ${te.wadd}`}
              aria-label={line ? T('Legg til aktivitet for hele vakten') : T('Legg til aktivitet')}
              onClick={() => startDraft(null)}
            >
              <Icon name="plus" />
              {T('Legg til')}
            </button>
          </div>
        </div>
      </div>
      {line && (
        <div
          ref={lineRef}
          className={`${te.tl}${drawing ? ` ${te.drawing}` : ''}${anyOpen ? ` ${te.held}` : ''}`}
          style={{ height: line.len * PX + sc.extra }}
          data-testid="task-timeline"
          data-keep
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={cancel}
        >
          {line.hours.map((x) => (
            <span key={x} className={te.hr} style={{ top: sc.y(x) }}>
              <span>{line.clock(x)}</span>
            </span>
          ))}
          {gaps.map((x, i) => (
            <button
              key={x}
              type="button"
              className={te.gap}
              data-gap
              tabIndex={i === 0 ? 0 : -1}
              style={{ top: sc.y(x) + 1, height: sc.yEnd(x + 30) - sc.y(x) - 2 }}
              aria-label={T('Legg til aktivitet {t}', { t: line.clock(x) })}
              onPointerDown={(e) => {
                // with something open, a press outside only closes it; the next one draws (owner 2026-10-27)
                // the timeline keeps its own presses (data-keep), so it closes here itself
                swallow.current = anyOpen;
                if (anyOpen) {
                  closeAll();
                  if (!term.trim()) dropDrafts(edits);
                  return;
                }
                // touch scrolls the panel; a tap is the half hour (click)
                if (e.button !== 0 || e.pointerType === 'touch') return;
                const a = Math.max(x, Math.min(x + 15, minAt(e.clientY)));
                const stop = stretchOf(busy, a, a, line.len).down;
                (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                draw.current = { a, b: Math.min(a + 30, stop), stop };
                setDrawing([a, Math.min(a + 30, stop)]);
              }}
              onClick={() => {
                if (swallow.current) {
                  swallow.current = false;
                  return;
                }
                if (drew.current) return;
                startDraft({ start_time: line.clock(x), end_time: line.clock(x + 30) });
              }}
              onKeyDown={(e) => {
                // one stop in the tab order; the arrows walk the free half hours
                if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
                e.preventDefault();
                const all = [...(lineRef.current?.querySelectorAll<HTMLElement>('[data-gap]') ?? [])];
                all[all.indexOf(e.currentTarget) + (e.key === 'ArrowDown' ? 1 : -1)]?.focus();
              }}
            >
              <Icon name="plus" />
              {line.clock(x)}
            </button>
          ))}
          {lunchSpan &&
            (onLunch ? (
              // the pause moves like a block, a quarter hour at a time, never onto an activity (#202)
              <button
                type="button"
                className={`${te.pause} ${te.pmove}`}
                data-testid="task-pause"
                style={{ top: sc.y(lunchSpan[0]), height: sc.yEnd(lunchSpan[1]) - sc.y(lunchSpan[0]) }}
                aria-label={T('Pause {t}, dra eller bruk piltastene for å flytte', {
                  t: `${line.clock(lunchSpan[0])}–${line.clock(lunchSpan[1])}`,
                })}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                  pauseDrag.current = { y: e.clientY, a: lunchSpan[0] };
                }}
                onPointerMove={(e) => {
                  const g = pauseDrag.current;
                  if (!g) return;
                  e.stopPropagation();
                  const at = pauseTo(lunchSpan, g.a + Math.round((e.clientY - g.y) / PX / 15) * 15);
                  if (at != null && at !== lunchSpan[0]) onLunch(line.clock(at));
                }}
                onPointerUp={() => {
                  pauseDrag.current = null;
                }}
                onPointerCancel={() => {
                  pauseDrag.current = null;
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
                  e.preventDefault();
                  const at = pauseTo(lunchSpan, lunchSpan[0] + (e.key === 'ArrowDown' ? 15 : -15));
                  if (at != null) onLunch(line.clock(at));
                }}
              >
                {T('Pause')} {line.clock(lunchSpan[0])}–{line.clock(lunchSpan[1])}
              </button>
            ) : (
              <span
                className={te.pause}
                style={{ top: sc.y(lunchSpan[0]), height: sc.yEnd(lunchSpan[1]) - sc.y(lunchSpan[0]) }}
              >
                {T('Pause')} {line.clock(lunchSpan[0])}–{line.clock(lunchSpan[1])}
              </span>
            ))}
          {ghosts
            .filter((g) => g.p.at)
            .map((g) => {
              const [a, b] = g.p.at as [number, number];
              const h = sc.yEnd(b) - sc.y(a);
              return (
                <button
                  key={`g-${g.name}`}
                  type="button"
                  className={`${te.blk} ${te.ghost}`}
                  style={{ top: sc.y(a), height: h, ...ghostTone(g) }}
                  data-testid="task-ghost"
                  title={T('{n} av {m} {day}', { n: g.n, m: past.length, day: ghostDay })}
                  aria-label={T('Legg til {task} {t}', {
                    task: g.name,
                    t: `${line?.clock(a)}–${line?.clock(b)}`,
                  })}
                  onClick={() => keep([g])}
                >
                  {h >= 24 && (
                    <span className={te.tile}>
                      <Icon name={ghostIcon(g)} />
                    </span>
                  )}
                  <span>
                    <b>{g.name}</b>
                    {h >= 44 && (
                      <small>
                        {line?.clock(a)}–{line?.clock(b)}
                      </small>
                    )}
                  </span>
                  <small className={te.gof}>{T('{n} av {m}', { n: g.n, m: past.length })}</small>
                  <Icon name="plus" />
                </button>
              );
            })}
          {timed.map((it) => {
            const [a, b] = span(it) as [number, number];
            if (isOpen(it.uid)) return openBox(it, { top: sc.y(a) });
            const h = sc.yEnd(b) - sc.y(a);
            const name = it.def.name.trim();
            const fin = done(it);
            return (
              <div
                key={it.uid}
                className={`${te.blk}${lateOf(it) ? ` ${te.late}` : ''}`}
                style={{ top: sc.y(a), height: h, ...toneFor(it.def, it.saved) }}
                data-testid="task-block"
                data-blk={it.uid}
                data-done={fin || undefined}
              >
                <button
                  type="button"
                  className={te.body}
                  aria-label={T('Rediger {task}', { task: name })}
                  aria-expanded={false}
                  onPointerDown={(e) => down(e, it, 'move')}
                  onClick={() => toggle(it.uid)}
                  onKeyDown={(e) => nudge(e, it)}
                >
                  {h >= 24 && (
                    <span className={te.tile}>
                      <Icon name={iconOf(name, it.saved, it.def.one_off)} />
                    </span>
                  )}
                  <span>
                    <b>{name}</b>
                    {h >= 44 && (
                      <small>
                        {clockOf(it.def)}
                        {it.def.description.trim() ? `, ${it.def.description.trim()}` : ''}
                      </small>
                    )}
                  </span>
                  {mark(it)}
                </button>
                {!fin && (
                  <>
                    <span className={te.top} aria-hidden="true" onPointerDown={(e) => down(e, it, 'top')} />
                    <span
                      className={te.bot}
                      aria-hidden="true"
                      onPointerDown={(e) => down(e, it, 'bottom')}
                    />
                  </>
                )}
              </div>
            );
          })}
          {drafts.map((it) => {
            const s = span(it);
            if (!s) return null;
            const h = draftH(s);
            return [
              <div
                key={`${it.uid}-at`}
                className={`${te.blk} ${te.draft}`}
                style={{ top: sc.y(s[0]), height: h }}
                data-testid="task-draft"
              >
                {/* the typed name and the icon it suggests, live, as the prototype draws it */}
                <Icon name={term.trim() ? activityIcon(term) : 'plus'} />
                <b>{term.trim() || T('Ny aktivitet')}</b>
                <small>{clockOf(it.def)}</small>
              </div>,
              draftBox(it, { top: sc.y(s[0]) + h + 4 }),
            ];
          })}
          {drawing && (
            <div
              className={te.hl}
              style={{ top: sc.y(drawing[0]), height: sc.yEnd(drawing[1]) - sc.y(drawing[0]) }}
            >
              {line.clock(drawing[0])}–{line.clock(drawing[1])}
            </div>
          )}
        </div>
      )}
      {tasks.map((a) =>
        edits.changed[a.id] === null ? (
          <div className="trow" key={a.id} data-testid="task-removed">
            <span className="tname">
              <b>
                <s>{a.shiftTask?.name ?? ''}</s>
              </b>
              <span>{T('Fjernes når du lagrer')}</span>
            </span>
            <span className="tstat">
              <Button
                small
                aria-label={T('Angre fjerning av {task}', { task: a.shiftTask?.name ?? '' })}
                onClick={() => setChanged(a.id, undefined)}
              >
                {T('Angre')}
              </Button>
            </span>
          </div>
        ) : null,
      )}
      {refused && problem && problem.uid === null ? (
        <p className="ferr" data-testid="task-problem">
          {problem.msg}
        </p>
      ) : null}
      {overlaps.length ? (
        <output className="hint warn" data-testid="task-overlap" style={{ display: 'block' }}>
          {T('Overlapper: {pairs}. Juster tiden om aktivitetene ikke kan gjøres samtidig.', {
            pairs: overlaps.map(([a, b]) => T('{a} og {b}', { a, b })).join('; '),
          })}
        </output>
      ) : null}
      <Dialog.Root open={!!newType} onOpenChange={(o) => !o && setNewType(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="scrim" />
          <Dialog.Content className={te.typeDlg} aria-describedby={undefined} data-testid="task-newtype">
            {newType &&
              (() => {
                const nt = newType;
                const set = (v: Partial<typeof nt>) => setNewType({ ...nt, ...v });
                const name = nt.name.trim();
                const twin = name
                  ? options.find((o) => o.name.toLowerCase() === name.toLowerCase() && !o.one_off)
                  : undefined;
                const once = listOf(onceAll.data)
                  .filter((o) => o.one_off)
                  .sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0))
                  .slice(0, 6);
                const used = (n?: number) =>
                  n === 1 ? T('brukt 1 gang') : T('brukt {n} ganger', { n: n ?? 0 });
                return (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!name) return;
                      // a name the library holds is used as it is, not made twice
                      if (twin) {
                        nt.apply(twin.name, nt.description, nt.time);
                        setNewType(null);
                        return;
                      }
                      const look = {
                        ...(nt.icon ? { icon: nt.icon } : {}),
                        ...(nt.color ? { color: nt.color } : {}),
                      };
                      try {
                        if (nt.id)
                          await libWrites.update.mutateAsync({
                            id: nt.id,
                            body: { name, one_off: false, ...look },
                          });
                        else
                          await libWrites.create.mutateAsync({
                            name,
                            ...(nt.description ? { description: nt.description } : {}),
                            ...look,
                          });
                      } catch {
                        toast(T('Kunne ikke lagre aktiviteten. Prøv igjen.'));
                        return;
                      }
                      nt.apply(name, nt.description, nt.time);
                      setNewType(null);
                    }}
                  >
                    <div className={te.typeHd}>
                      <Dialog.Title>{T('Lagre som type')}</Dialog.Title>
                      <Dialog.Close asChild>
                        <button type="button" className={te.typeX} aria-label={T('Lukk')}>
                          <Icon name="close" />
                        </button>
                      </Dialog.Close>
                    </div>
                    {once.length > 0 && (
                      <div className={te.typeSec}>
                        <span className={te.typeLbl}>{T('Ikke lagret som type')}</span>
                        <div className={te.typeChips}>
                          {once.map((o) => (
                            <button key={o.id} type="button" onClick={() => set({ name: o.name })}>
                              {o.name}, {used(o.usage_count)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className={te.typeSec}>
                      <span className={te.typeLbl}>{T('Navn')}</span>
                      <input
                        className="inp w"
                        // biome-ignore lint/a11y/noAutofocus: the dialog opens to name the new activity
                        autoFocus
                        placeholder={T('F.eks. Bestilling')}
                        value={nt.name}
                        onChange={(e) => set({ name: e.target.value })}
                      />
                    </label>
                    {twin && (
                      <p className={te.typeTwin}>
                        {T('Finnes allerede: {name}. Lagre bruker den i stedet.', { name: twin.name })}
                      </p>
                    )}
                    <div className={te.typeSec}>
                      <span className={te.typeLbl}>{T('Ikon')}</span>
                      <div className={te.typeIcons}>
                        {ICONS.slice(0, 16).map((ic) => (
                          <button
                            key={ic}
                            type="button"
                            aria-label={iconName(ic)}
                            aria-pressed={activityIcon(nt.name, false, nt.icon) === ic}
                            onClick={() => set({ icon: ic })}
                          >
                            <Icon name={ic} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className={te.typeSec}>
                      <span className={te.typeLbl}>{T('Farge')}</span>
                      <div className={te.typeSw}>
                        {COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            aria-label={c}
                            aria-pressed={colorOf(nt.color) === c}
                            style={toneOf(c) as React.CSSProperties}
                            onClick={() => set({ color: c })}
                          />
                        ))}
                      </div>
                    </div>
                    <div className={te.typeSec}>
                      <span className={te.typeLbl}>{T('Tid på denne vakten')}</span>
                      <div className={te.typeTime}>
                        {nt.time ? (
                          <>
                            <input
                              className="inp"
                              type="time"
                              aria-label={T('Fra')}
                              value={nt.time.from}
                              onChange={(e) => set({ time: { from: e.target.value, to: nt.time?.to ?? '' } })}
                            />
                            <span>{T('til')}</span>
                            <input
                              className="inp"
                              type="time"
                              aria-label={T('Til')}
                              value={nt.time.to}
                              onChange={(e) =>
                                set({ time: { from: nt.time?.from ?? '', to: e.target.value } })
                              }
                            />
                          </>
                        ) : (
                          <span className={te.typeMuted}>{T('Hele vakten')}</span>
                        )}
                        <button
                          type="button"
                          className="lnk"
                          onClick={() => set({ time: nt.time ? null : { from: from ?? '', to: to ?? '' } })}
                        >
                          {nt.time ? T('Hele vakten') : T('Gi fast tid')}
                        </button>
                      </div>
                    </div>
                    <input
                      className="inp w"
                      aria-label={T('Beskrivelse')}
                      placeholder={T('Beskrivelse (valgfritt)')}
                      value={nt.description}
                      onChange={(e) => set({ description: e.target.value })}
                    />
                    <div className={te.typeFoot}>
                      <Dialog.Close asChild>
                        <button type="button" className="lnk">
                          {T('Avbryt')}
                        </button>
                      </Dialog.Close>
                      <Button tone="pri" type="submit" disabled={!name}>
                        {twin ? T('Legg til') : T('Lagre som type')}
                      </Button>
                    </div>
                  </form>
                );
              })()}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
